/* storybook-check-ignore */
import * as React from 'react';

import { CTAProps } from '@opendoor/bricks/complex/CtaInput';
// TODO: Label should not be used directly and should stay as an internal utility for bricks form components. Please update the usage of Label.
// eslint-disable-next-line no-restricted-imports
import { Label, LabelProps } from '@opendoor/bricks/core';
import { IAddressInputAddress } from '@opendoor/cinderblocks/growth/AddressInput/AddressInput';
import { EventTrackingAction } from '@opendoor/observability/slim';

import {
  OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';

import { useObservability } from '../../helpers/observability';
// TODO(betty): Move new address input component into Cinderblocks after React 18 upgrade
// import { AddressInput, IAddressInputProps } from '@opendoor/cinderblocks/growth/AddressInput';
import { startAddressVerification } from '../api/addressInputHelpers';
import { CONSUMER_FE_URL, COSMOS_URL } from '../globals';
import AddressInput, { IProps as IAddressInputProps } from './NewAddressInput';
import { setCookie } from './trackers';

export type { IAddressInputAddress };

type IAddressInputPropsWithoutCallbacks = Omit<
  IAddressInputProps,
  'onSubmit' | 'onChooseManualEntry' | 'googleMapsApiKey' | 'analyticsName'
>;
export interface SellerFlowAddressInputProps extends IAddressInputPropsWithoutCallbacks {
  trackingKeywords?: string;
  trackingTaxonomy?: string;
  inputLocation?: string;
  ctaProps: CTAProps;
  hideLabel?: boolean;
  // We only want to display above for sticky CTA mobile
  displayAbove?: boolean;
  placeholderText?: string;
  /**
   * Name of the address input in analytics events, should be unique within the app
   * Convention: {appName}-{addressInputName}
   */
  analyticsName: string;
  product: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product;
  /** Whether to show label in dark theme (white color) or not */
  labelProps?: LabelProps;
  /**
   * Override the onSuccess callback to do something other than redirect to reception-fe
   */
  onSuccess?: (address: IAddressInputAddress, token: string, pdpUrl?: string) => void;
  /**
   * Override the onChooseManualEntry callback to do something other than redirect to reception-fe
   */
  onChooseManualEntry?: IAddressInputProps['onChooseManualEntry'];
}

export const handleAddressSubmit = async (
  address: IAddressInputAddress,
  trackEvent: ReturnType<typeof useObservability>['trackEvent'],
  product: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
  trackingTaxonomy?: string,
  channel?: OdProtosSellReceptionData_SellerInput_Channel,
  trackingKeywords?: string,
  inputLocation?: string,
  onSuccess?: SellerFlowAddressInputProps['onSuccess'],
) => {
  // Setting cookie to know where the customer is coming from in reception-fe for experiments
  if (trackingTaxonomy) {
    setCookie('sourceForTracking', trackingTaxonomy, 1);
  }

  const error = await startAddressVerification({
    address,
    product,
    channel,
    trackingKeywords,
    inputLocation,
    onSuccess: (token, pdpUrl) => {
      if (pdpUrl) {
        trackEvent('inputting_address_success' as EventTrackingAction, 'pdp-redirect');
        if (onSuccess) {
          return onSuccess(address, token, pdpUrl);
        }
        window.location.href = `${COSMOS_URL}${pdpUrl}?utm_source=seller&utm_medium=web&utm_campaign=ae_pdp`;
      } else {
        trackEvent('inputting_address_success' as EventTrackingAction, 'onboarding-redirect');
        if (onSuccess) {
          return onSuccess(address, token, pdpUrl);
        }
        window.location.href = `${CONSUMER_FE_URL}/seller-lead/${token}`;
      }
    },
  });
  if (error) {
    if (error.type === 'invalid-address') {
      // if errored due to invalid argument, redirect to manual address entry
      startManualAddressVerification(channel, trackingKeywords);
    } else {
      window.location.href = '/error';
    }
  }
};

const startManualAddressVerification = (channel?: string, trackingKeywords?: string) => {
  const path = `${CONSUMER_FE_URL}/seller-lead/address-manual?type=new`;
  const searchParams = new URLSearchParams();
  if (channel) {
    searchParams.set('channel', channel);
  }
  if (trackingKeywords) {
    searchParams.set('keywords', trackingKeywords);
  }

  const params = searchParams.toString();
  window.location.assign(path + (params ? `?${params}` : ''));
};

export const SellerFlowAddressInput: React.FC<SellerFlowAddressInputProps> = ({
  className,
  inputLocation,
  trackingKeywords,
  trackingTaxonomy,
  channel,
  hideLabel,
  displayAbove,
  placeholderText,
  product,
  labelProps,
  onChooseManualEntry,
  onSuccess,
  showError = false,
  ...others
}) => {
  const { trackEvent } = useObservability();

  return (
    <>
      {!hideLabel && (
        <Label
          display="inline-block"
          mb="4"
          fontWeight="semibold"
          color="neutrals0"
          id="address-input-label"
          fontSize="s0"
          htmlFor="address-input"
          {...labelProps}
        >
          Enter your home address
        </Label>
      )}
      <AddressInput
        ariaLabelledby="address-input-label"
        aria-label="Enter your home address"
        showError={showError}
        className={className}
        placeholderText={placeholderText ?? '125 W Muriel Dr'}
        onSubmit={(address: IAddressInputAddress) => {
          handleAddressSubmit(
            address,
            trackEvent,
            product,
            trackingTaxonomy,
            channel,
            trackingKeywords,
            inputLocation,
            onSuccess,
          );
        }}
        onChooseManualEntry={
          onChooseManualEntry ||
          function () {
            return startManualAddressVerification(channel, trackingKeywords);
          }
        }
        onValidate={(isValid) => {
          if (isValid) {
            return;
          }
          // onValidate is called if we get a correct address back from google
          // but then the place_id verification fails, example is:
          // 1673 Sunburst St, Northridge, CA, USA
          onChooseManualEntry
            ? onChooseManualEntry(channel)
            : startManualAddressVerification(channel, trackingKeywords);
        }}
        channel={channel}
        optionsDisplayProps={{ displayAbove: displayAbove }}
        {...others}
      />
    </>
  );
};
